/**
 * Loads the captain's model the way the game loads its HQ art (render/assets.ts: GLTFLoader with the meshopt decoder,
 * URLs through the single-file build's inlined store), falling back to the troop-soldier stand-in while
 * `art/hq/guide/captain_bram.glb` has not landed. The model is normalised to the contract's frame (1 tall, feet at 0,
 * centred) under a holder the animator moves for hops, leans and squashes.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { AssetLibrary } from "../../../render/assets";
import { BRAM_GLB, BRAM_PLACEHOLDER_GLB } from "./bramContract";
import { bindRig, type BramRig } from "./bramRig";
import { recordDiag } from "../../../infra/diagLog";

export interface BramModel {
  /** Add this to the scene; the animator moves it (hop, lean, squash, entrance). */
  readonly holder: THREE.Group;
  readonly rig: BramRig;
  /** Where the head meets the body (normalised: he is 1 tall), the framing's reference. */
  readonly neckY: number;
  /** Width of the head (with the helmet), so a narrow frame still fits it. */
  readonly headWidth: number;
}

/** The contract's proportions when the model has no `head` node: the head is the top third. */
const DEFAULT_NECK_Y = 0.67;
const DEFAULT_HEAD_WIDTH = 0.42;
/** The troop soldier faces -Z (the contract's model faces +Z): the stand-in is turned round. */
const STAND_IN_YAW = Math.PI;

let loader: GLTFLoader | null = null;
function gltfLoader(): GLTFLoader {
  if (!loader) {
    loader = new GLTFLoader();
    loader.manager.setURLModifier((url) => AssetLibrary.resolveInlined(url));
    loader.setMeshoptDecoder(MeshoptDecoder);
  }
  return loader;
}

function normalise(scene: THREE.Object3D, animations: THREE.AnimationClip[], yaw: number): BramModel {
  const turn = new THREE.Group();
  turn.rotation.y = yaw;
  turn.add(scene);
  const norm = new THREE.Group();
  norm.add(turn);
  norm.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene);
  const h = Math.max(1e-3, box.max.y - box.min.y);
  const s = 1 / h;
  norm.scale.setScalar(s);
  norm.position.set(-((box.min.x + box.max.x) / 2) * s, -box.min.y * s, -((box.min.z + box.max.z) / 2) * s);
  const holder = new THREE.Group();
  holder.name = "bram.holder";
  holder.add(norm);
  holder.updateMatrixWorld(true);
  const rig = bindRig(scene, animations);
  let neckY = DEFAULT_NECK_Y, headWidth = DEFAULT_HEAD_WIDTH;
  const head = rig.bones.head;
  if (head) {
    // a head bone's joint is the neck; a rigid head part (the stand-in) has its own box, whose bottom is the neck
    const hb = (head as THREE.Bone).isBone ? new THREE.Box3() : new THREE.Box3().setFromObject(head);
    if (hb.isEmpty()) neckY = head.getWorldPosition(new THREE.Vector3()).y;
    else { neckY = hb.min.y; headWidth = Math.max(hb.max.x - hb.min.x, hb.max.z - hb.min.z); }
  }
  scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.frustumCulled = false; m.castShadow = false; m.receiveShadow = false; } });
  return { holder, rig, neckY, headWidth };
}

/**
 * Waits before each retry of the captain's model. A phone on a tunnel link drops a 2 MB download now and then; the
 * old code then fell straight to the troop stand-in — which, since the troops became armoured knights, put a knight in
 * Captain Bram's welcome card (Kevin, 2026-10-01). Now the real model is retried, and if it still fails the stage
 * shows Bram's still renders (bramFallback), never another character.
 */
const RETRY_DELAYS_MS = [800, 2500];

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

async function loadCaptain(): Promise<BramModel> {
  let last: unknown = null;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await wait(RETRY_DELAYS_MS[attempt - 1]);
    try {
      const gltf = await gltfLoader().loadAsync(BRAM_GLB);
      return normalise(gltf.scene, gltf.animations, 0);
    } catch (err) {
      last = err;
      recordDiag("warning", `captain: model load ${attempt + 1} failed: ${String((err as Error)?.message ?? err).slice(0, 200)}`);
    }
  }
  throw last ?? new Error("no captain model");
}

/** The troop stand-in (dev tools only, e.g. before the captain's GLB exists in a fresh checkout). */
export async function loadStandIn(): Promise<BramModel> {
  const gltf = await gltfLoader().loadAsync(BRAM_PLACEHOLDER_GLB);
  return normalise(gltf.scene, gltf.animations, STAND_IN_YAW);
}

let pending: Promise<BramModel> | null = null;

/** The captain's model (loaded once; `forgetBram` drops it). Rejects when it does not load: the stage then shows his stills. */
export function loadBram(): Promise<BramModel> {
  pending ??= loadCaptain();
  pending.catch(() => { pending = null; });
  return pending;
}

/** Frees the model's GPU and CPU copies; the next `loadBram` fetches it again (from the offline cache). */
export function forgetBram(model: BramModel | null): void {
  pending = null;
  if (!model) return;
  const texs = new Set<THREE.Texture>();
  model.holder.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      for (const v of Object.values(m)) if ((v as THREE.Texture | null)?.isTexture) texs.add(v as THREE.Texture);
      m.dispose();
    }
    (mesh as THREE.SkinnedMesh).skeleton?.dispose();
  });
  for (const t of texs) t.dispose();
  model.holder.removeFromParent();
}
